<template>
  <section id="contacto" class="section-padding bg-gray-50">
    <div class="container-custom">
      <!-- Title -->
      <div class="text-center mb-12 animate-fade-in-up">
        <h2 class="section-title">{{ $t('contact.title') }}</h2>
        <p class="section-subtitle mt-4">
          {{ $t('contact.subtitle') }}
        </p>
      </div>

      <!-- Form Container -->
      <div class="max-w-2xl mx-auto">
        <form
          @submit.prevent="handleSubmit"
          class="card p-8 md:p-12 space-y-6 animate-fade-in-up delay-100"
        >
          <!-- Name -->
          <div>
            <label for="name" class="block text-sm font-medium text-navy-500 mb-2">
              {{ $t('contact.form.name') }}
            </label>
            <input
              id="name"
              v-model="formData.name"
              type="text"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              :placeholder="$t('contact.form.name')"
            />
          </div>

          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-navy-500 mb-2">
              {{ $t('contact.form.email') }}
            </label>
            <input
              id="email"
              v-model="formData.email"
              type="email"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              :placeholder="$t('contact.form.email')"
            />
          </div>

          <!-- Phone -->
          <div>
            <label for="phone" class="block text-sm font-medium text-navy-500 mb-2">
              {{ $t('contact.form.phone') }}
            </label>
            <input
              id="phone"
              v-model="formData.phone"
              type="tel"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              :placeholder="$t('contact.form.phone')"
            />
          </div>

          <!-- Message -->
          <div>
            <label for="message" class="block text-sm font-medium text-navy-500 mb-2">
              {{ $t('contact.form.message') }}
            </label>
            <textarea
              id="message"
              v-model="formData.message"
              rows="5"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
              :placeholder="$t('contact.form.message')"
            ></textarea>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="isSubmitting"
            :class="[
              'btn-primary w-full flex items-center justify-center gap-2',
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            ]"
          >
            <span v-if="!isSubmitting">{{ $t('contact.form.submit') }}</span>
            <span v-else>Enviando...</span>
            <font-awesome-icon
              v-if="!isSubmitting"
              icon="arrow-right"
              class="transition-transform group-hover:translate-x-1"
            />
          </button>

          <!-- Success Message -->
          <transition name="fade">
            <div
              v-if="showSuccess"
              class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-3"
            >
              <font-awesome-icon icon="check-circle" class="text-green-500" />
              <span>¡Mensaje enviado exitosamente! Nos contactaremos pronto.</span>
            </div>
          </transition>

          <!-- Error Message -->
          <transition name="fade">
            <div
              v-if="showError"
              class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
            >
              Hubo un error al enviar el mensaje. Por favor intenta nuevamente.
            </div>
          </transition>
        </form>

        <!-- Contact Info Cards -->
        <div class="grid md:grid-cols-2 gap-6 mt-12">
          <!-- WhatsApp -->
          <a
            href="https://wa.me/56992345248"
            target="_blank"
            rel="noopener noreferrer"
            class="card p-6 flex items-center gap-4 hover:shadow-xl transition-all group"
          >
            <div class="w-12 h-12 rounded-full gradient-corporate flex items-center justify-center flex-shrink-0">
              <font-awesome-icon :icon="['fab', 'whatsapp']" class="text-white text-2xl" />
            </div>
            <div>
              <div class="font-semibold text-navy-500 group-hover:text-primary transition-colors">
                WhatsApp
              </div>
              <div class="text-gray-600">+569 9234 5248</div>
            </div>
          </a>

          <!-- Email -->
          <a
            href="mailto:ventas@motoboy.cl"
            class="card p-6 flex items-center gap-4 hover:shadow-xl transition-all group"
          >
            <div class="w-12 h-12 rounded-full gradient-corporate flex items-center justify-center flex-shrink-0">
              <font-awesome-icon icon="envelope" class="text-white text-2xl" />
            </div>
            <div>
              <div class="font-semibold text-navy-500 group-hover:text-primary transition-colors">
                Email
              </div>
              <div class="text-gray-600">ventas@motoboy.cl</div>
            </div>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';

const formData = reactive({
  name: '',
  email: '',
  phone: '',
  message: '',
});

const isSubmitting = ref(false);
const showSuccess = ref(false);
const showError = ref(false);

const handleSubmit = async () => {
  isSubmitting.value = true;
  showSuccess.value = false;
  showError.value = false;

  try {
    // Detectar endpoint según ambiente
    // Why: api.motoboychile.app NO apunta a coruscant — sirve la landing como
    // fallback HTML y el POST nunca llegaba al backend. coruscant.motoboychile.app
    // es el host real del API en prod. Dev: puerto 3011 (era 3010, +1 para evitar
    // choque entre ambientes — ver CLAUDE.md sección Puertos locales).
    const apiBaseUrl = import.meta.env.DEV
      ? 'http://localhost:3011'
      : 'https://coruscant.motoboychile.app';

    // Enviar formulario al endpoint
    const response = await fetch(`${apiBaseUrl}/contact-form`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Error al enviar el formulario');
    }

    // Éxito
    showSuccess.value = true;

    // Reset form
    Object.keys(formData).forEach(key => {
      formData[key as keyof typeof formData] = '';
    });

    // Ocultar mensaje después de 5 segundos
    setTimeout(() => {
      showSuccess.value = false;
    }, 5000);
  } catch (error) {
    console.error('Error al enviar formulario:', error);
    showError.value = true;

    setTimeout(() => {
      showError.value = false;
    }, 5000);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
